import { Grid, Stack } from '@mui/material';

import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Controller, useForm } from 'react-hook-form';

import ButtonComp from 'common/components/Button/ButtonComp';
import DatepickerComponent from 'common/components/Datepicker/Datepicker';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { RootState } from 'common/store/AppStore';
import {
	setParserDocumentInfo,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import {
	getDateStringFromISOString,
	toSentenceCase,
} from 'common/utils/utilFunctions';

import { IDocumentInfoElements } from 'features/DataIngestion/api/type-def';

import styles from './ParseRegulationTabInfo.module.scss';

const DocumentInfo = () => {
	const dispatch = useDispatch();

	const parserDocumentInfo = useSelector(
		(state: RootState) => state.common.parserDocumentInfo,
	);

	const defaultValues = {
		bookTitle: parserDocumentInfo.bookTitle,
		issuer: parserDocumentInfo.issuer,
		issuanceType: parserDocumentInfo.issuanceType,
		jurisdiction: parserDocumentInfo.jurisdiction,
		issuanceDate: parserDocumentInfo.issuanceDate,
		publishedDate: parserDocumentInfo.publishedDate,
	};

	const {
		control,
		trigger,
		setValue,
		getValues,
		formState: { isDirty, isValid },
	} = useForm<IDocumentInfoElements>({
		mode: 'onChange',
		defaultValues,
	});

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const onDateChangeHandler =
		(key: keyof IDocumentInfoElements) => (val: string | null) => {
			setValue(key, val ?? '', { shouldDirty: true });
			trigger(key);
		};

	const onSaveHandler = () => {
		dispatch(
			setToastMessage({
				message: 'Document details saved successfully',
				severity: 'success',
				isToastOpen: true,
			}),
		);
		dispatch(setParserDocumentInfo(getValues()));
	};

	const handleClose = useCallback(() => {
		dispatch(
			setToastMessage({
				isToastOpen: false,
				message: '',
				severity: '',
			}),
		);
	}, []);

	return (
		<Grid item xs={12} sm={12} md={12} xl={12} lg={12}>
			<form>
				<Grid container>
					<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1} mt={2}>
						<Controller
							name="bookTitle"
							control={control}
							rules={{
								required: {
									value: true,
									message: 'Please enter book title',
								},
								validate: (value) => !!value.trim(),
							}}
							render={({
								field: { name, value, onChange },
								fieldState: { error },
							}) => (
								<TextFieldWithLabel
									label={toSentenceCase('Book Title')}
									name={name}
									isFilled
									showLabel
									id="book-title"
									placeholder="Enter the book title"
									value={value}
									inputProps={{
										'data-testid': 'book-title',
									}}
									onChange={onChange}
									error={!!error}
									required
									helperText={error ? error.message : null}
									className={styles['task-field-width']}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
						<Controller
							name="issuer"
							control={control}
							rules={{
								required: {
									value: true,
									message: 'Please enter issuer',
								},
								validate: (value) => !!value.trim(),
							}}
							render={({
								field: { name, value, onChange },
								fieldState: { error },
							}) => (
								<TextFieldWithLabel
									label={toSentenceCase('Issuer')}
									name={name}
									id="issuer-name"
									isFilled
									showLabel
									placeholder="Enter the issuer"
									value={value}
									inputProps={{
										'data-testid': 'issuer',
									}}
									onChange={onChange}
									error={!!error}
									required
									helperText={error ? error.message : null}
									className={styles['task-field-width']}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
						<Controller
							name="issuanceType"
							control={control}
							rules={{
								required: {
									value: true,
									message: 'Please enter issuanceType',
								},
								validate: (value) => !!value.trim(),
							}}
							render={({
								field: { name, value, onChange },
								fieldState: { error },
							}) => (
								<TextFieldWithLabel
									label={toSentenceCase('Issuance Type')}
									name={name}
									id="issuer-name"
									placeholder="Enter the issuance type"
									value={value}
									isFilled
									showLabel
									inputProps={{
										'data-testid': 'issuance type',
									}}
									onChange={onChange}
									error={!!error}
									required
									helperText={error ? error.message : null}
									className={styles['task-field-width']}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
						<Controller
							name="jurisdiction"
							control={control}
							rules={{
								required: {
									value: true,
									message: 'Please enter jurisdiction',
								},
								validate: (value) => !!value.trim(),
							}}
							render={({
								field: { name, value, onChange },
								fieldState: { error },
							}) => (
								<TextFieldWithLabel
									label={toSentenceCase('Jurisdiction')}
									name={name}
									isFilled
									showLabel
									id="jurisdiction-name"
									placeholder="Enter the jurisdiction"
									value={value}
									inputProps={{
										'data-testid': 'jurisdiction',
									}}
									onChange={onChange}
									error={!!error}
									required
									helperText={error ? error.message : null}
									className={styles['task-field-width']}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
						<Controller
							name="issuanceDate"
							control={control}
							rules={{
								required: {
									value: true,
									message: 'Please enter issuance date',
								},
							}}
							render={({ field: { name, value } }) => (
								<DatepickerComponent
									propType={{
										label: toSentenceCase('Issuance Date'),
										disablePast: true,
										name: { name },
										id: 'dueDate',
										required: true,
										className: styles['task-field-width'],
										inputProps: {
											'data-testid': 'issuance-dueDate',
										},
									}}
									showLabel
									onChange={onDateChangeHandler(name)}
									dateValue={getDateStringFromISOString(value)}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
						<Controller
							name="publishedDate"
							control={control}
							rules={{
								required: {
									value: true,
									message: 'Please enter published Date',
								},
							}}
							render={({ field: { name, value } }) => (
								<DatepickerComponent
									propType={{
										label: toSentenceCase('Published Date'),
										disablePast: true,
										name: { name },
										id: 'dueDate',
										required: true,
										className: styles['task-field-width'],
										inputProps: {
											'data-testid': 'published-Date',
										},
									}}
									onChange={onDateChangeHandler(name)}
									dateValue={getDateStringFromISOString(value)}
								/>
							)}
						/>
					</Grid>
				</Grid>
			</form>
			<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
				<Stack direction="row" className={styles['btn-task-button']}>
					<ButtonComp
						btnType="primary"
						clickHandler={onSaveHandler}
						disabled={!isDirty || !isValid}
						data-testid="save-btn"
						compClass={styles['btn-save']}
					>
						Save
					</ButtonComp>
				</Stack>
			</Grid>
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleClose}
				vertical="top"
				horizontal="right"
			/>
		</Grid>
	);
};

export default DocumentInfo;
